import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import axiosClient from "../../api/axiosClient";
import { getPropertyDocumentDownload } from "../../api/documentApi";
import DataTable from "../brand/DataTable";
import { formatDateUk } from "../../utils/dateDisplay";
import OwnerAccountsUpload from "./OwnerAccountsUpload";

export default function PropertyAccountsTab({ propertyId, canEdit, onMessage }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const load = useCallback(async (nextPage) => {
    const response = await axiosClient.get(`/properties/${propertyId}/owner-accounts/`, {
      params: { page: nextPage + 1, pageSize },
    });
    setRows(response.data.results || []);
    setTotal(response.data.total || 0);
  }, [propertyId]);

  useEffect(() => {
    load(page).catch(() => onMessage?.("Could not load accounts.", "error"));
  }, [load, page, onMessage]);

  const download = async (row) => {
    const response = await getPropertyDocumentDownload(propertyId, row.id);
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = row.file_name || "accounts";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Accounts
      </Typography>
      {canEdit && (
        <OwnerAccountsUpload
          propertyId={propertyId}
          onMessage={onMessage}
          onUploaded={() => {
            setPage(0);
            load(0);
          }}
        />
      )}
      <DataTable size="small">
        <TableHead>
          <TableRow>
            <TableCell>Year</TableCell>
            <TableCell>File</TableCell>
            <TableCell>Uploaded</TableCell>
            <TableCell align="right">Download</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>No accounts published for this property.</TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.accounts_year}</TableCell>
              <TableCell>{row.file_name}</TableCell>
              <TableCell>{row.uploaded_at ? formatDateUk(row.uploaded_at) : "—"}</TableCell>
              <TableCell align="right">
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => download(row)}>
                  Download
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DataTable>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10]}
        onPageChange={(_, value) => setPage(value)}
      />
    </Paper>
  );
}
